import { INTERVENTION_NARRATIVE_FIELDS } from '../lib/interventionAssessmentNarratives';
const normalizedId = value => value === null || value === undefined ? '' : String(value);
export const interventionAssessmentScope = ({ caseId, applicationId, planId, interventionId }) =>
  JSON.stringify([caseId, applicationId, planId, interventionId].map(normalizedId));

export const packInterventionAssessmentDraft = (scope, form, baseline) => ({
  version: 1, scope, form, baseline,
});

const equal = (left, right) => JSON.stringify(left) === JSON.stringify(right);
const object = value => value && typeof value === 'object' && !Array.isArray(value);
const identifiedItems = value => Array.isArray(value) && value.every(item => object(item) && item.id != null) &&
  new Set(value.map(item => normalizedId(item.id))).size === value.length;

// Three-way merge: only local edits override fresh saved facts. Arrays of
// interventions/costs follow their item identities, never their row position.
const mergeEdits = (current, baseline, edited) => {
  if (equal(baseline, edited)) return current;
  if (identifiedItems(current) && identifiedItems(baseline) && identifiedItems(edited)) {
    const byId = items => new Map(items.map(item => [normalizedId(item.id), item]));
    const before = byId(baseline);
    const after = byId(edited);
    const result = current.filter(item => !before.has(normalizedId(item.id)) || after.has(normalizedId(item.id)))
      .map(item => before.has(normalizedId(item.id)) && after.has(normalizedId(item.id))
        ? mergeEdits(item, before.get(normalizedId(item.id)), after.get(normalizedId(item.id))) : item);
    const present = new Set(result.map(item => normalizedId(item.id)));
    return [...result, ...edited.filter(item => !before.has(normalizedId(item.id)) && !present.has(normalizedId(item.id)))];
  }
  if (object(current) && object(baseline) && object(edited)) {
    const result = { ...current };
    for (const key of new Set([...Object.keys(baseline), ...Object.keys(edited)])) {
      if (equal(baseline[key], edited[key])) continue;
      if (!Object.prototype.hasOwnProperty.call(edited, key)) delete result[key];
      else result[key] = mergeEdits(current[key], baseline[key], edited[key]);
    }
    return result;
  }
  return edited;
};

// Old, unowned caches were writable under the wrong selection. Their key alone
// cannot establish provenance, so never overlay them on the saved assessment.
export const restoreInterventionAssessmentDraft = (stored, scope, baseline, { revision = false } = {}) => {
  if (stored?.version !== 1 || stored.scope !== scope || !stored.form || !stored.baseline) return null;
  if (normalizedId(stored.form.actionPlanId) !== normalizedId(baseline.actionPlanId)) return null;
  if (revision) {
    const ids = form => (form.proposedInterventions || []).map(item => normalizedId(item.id));
    if (JSON.stringify(ids(stored.form)) !== JSON.stringify(ids(baseline)) ||
        JSON.stringify(ids(stored.baseline)) !== JSON.stringify(ids(baseline))) return null;
  }
  // Preserve genuinely edited fields on a same-record refresh; an old browser
  // copy must not replace unchanged dates/costs newly received from the server.
  const restored = Object.fromEntries(Object.entries(stored.form).filter(([key, value]) =>
    key !== 'actionPlanId' && !equal(value, stored.baseline[key]))
    .map(([key, value]) => [key, mergeEdits(baseline[key], stored.baseline[key], value)]));
  // A reopened local draft has already loaded its scoped narrative defaults.
  // Preserve that readiness and wording so the async prefill cannot overwrite
  // text the caseworker has edited while moving between their own proposals.
  if (!baseline.narrativeScope && stored.form.narrativeScope) {
    restored.narrativeScope = stored.form.narrativeScope;
    restored.narrativesFromLegacySnapshot = stored.form.narrativesFromLegacySnapshot;
    for (const { key } of INTERVENTION_NARRATIVE_FIELDS) restored[key] = stored.form[key];
  }
  return restored;
};
